import { Text, XStack, YStack } from 'tamagui'

import { useCurrentLesson } from '@hooks/useCurrentLesson'

import ActivityPage from '@components/Activity/ActivityPage'
import { HeroIcon } from '@components/HeroIcon'
import { MediaRender } from '@components/MediaRender'
import { useMediaSize } from '@hooks/useMediaSize'
import { useLessonTabStore } from '@stores/useLessonTabStore'
import { NotSupported } from '../extra_contents/NotSupported'
import { CourseContentNavigation } from './CourseContentNavigation'
import { MediaRenderWrapper } from './MediaRenderWrapper'
import { TabsContentContainer } from './TabsContentContainer'
import { useTinyPlayer } from '@hooks/useTinyPlayer'
import {
  GestureResponderEvent,
  Pressable,
  useWindowDimensions,
} from 'react-native'
import { useScrollControl } from '@hooks/useScrollControl'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useUpdateLessonProgress } from '@services/api/graphql/lessonProgresses'
import { useEnrollment } from '@hooks/useEnrollment'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { ILessonsProgresses } from '@dtos/Enrollment'
import { useGetCourseContent } from '@services/api/graphql/getCourseContent'
import { useLocalSearchParams } from 'expo-router'
import { useGetNextAvailableLessonData } from '@services/api/graphql/nextAvailableLessonData'
import { useToastController } from '@tamagui/toast'
import { useGetPreviousAvailableLessonData } from '@services/api/graphql/previousAvailableLessonData'

export interface IScreenProps {
  hasPreviousContent: boolean
  hasNextContent: boolean
  isCollapsibleOpen?: boolean
  toggleCollapsible?: () => void
  resetNextAndPreviousContent: () => void
}

export interface IFinishLessonButtonStyle {
  iconColor: string
  icon: IconProp
  label: string
}

export function MobileLayout({
  hasPreviousContent,
  hasNextContent,
  resetNextAndPreviousContent,
}: IScreenProps) {
  const [isLoadingNextOrPreviousLesson, setIsLoadingNextOrPreviousLesson] =
    useState(false)
  const [isLessonCompleted, setIsLessonCompleted] = useState(false)
  const { handleUpdateLessonProgress } = useUpdateLessonProgress()
  const { setIsScrollingContent } = useScrollControl()
  const { currentTab, setCurrentTab } = useLessonTabStore()
  const { isTinyPlayer } = useTinyPlayer()
  const { enrollment, updateEnrollmentState } = useEnrollment()
  const { height } = useWindowDimensions()
  const { currentLesson, setCurrentLesson } = useCurrentLesson()
  const { getNextAvailableLessonData } = useGetNextAvailableLessonData(
    currentLesson?.id ?? 0,
    enrollment?.id ?? 0,
  )
  const { getPreviousAvailableLessonData } = useGetPreviousAvailableLessonData(
    currentLesson?.id ?? 0,
    enrollment?.id ?? 0,
  )
  const mediaSize = useMediaSize()
  const { courseId, enrollmentId } = useLocalSearchParams()
  const { data: courseContent } = useGetCourseContent({
    id: Number(currentLesson?.courseContentId) ?? 0,
    courseId: Number(courseId) ?? 0,
    enrollmentId: Number(enrollmentId) ?? 0,
  })
  const toast = useToastController()

  const hasDescription = Boolean(currentLesson?.description)
  const hasExtraContent = Boolean((currentLesson?.extraMedias?.length ?? 0) > 0)
  const lessonWithoutContent = !hasDescription && !hasExtraContent
  const paddingBottom = lessonWithoutContent ? height * 0.125 : 0
  const extraMedia = currentLesson?.media ?? null
  const isWebinar = currentLesson?.media?.type === 'WebinarHerospark'
  const isMediaToUpdateOnAccess = ['Text', 'Document', 'Image'].includes(
    currentLesson?.media?.type ?? '',
  )
  const shouldUpdateLessonProgressOnView =
    (isWebinar || isMediaToUpdateOnAccess) && !isLessonCompleted
  const isActivity = !!(
    currentLesson?.activity?.examQuestionsCount &&
    currentLesson?.activity?.examQuestionsCount > 0
  )

  const currentLessonProgress =
    enrollment?.progressCard?.lessonsProgresses?.find(
      (lesson) => lesson.lessonId === currentLesson?.id,
    )

  const startMediaFrom = useMemo(() => {
    return isLessonCompleted ? 0 : currentLessonProgress?.currentVideoTime
  }, [currentLessonProgress?.currentVideoTime, isLessonCompleted])

  const finishLessonButtonStyle: IFinishLessonButtonStyle = useMemo(() => {
    return isLessonCompleted
      ? {
          iconColor: '$success-300',
          icon: ['fas', 'check-circle'],
          label: 'Concluído',
        }
      : {
          iconColor: '$neutral-600',
          icon: ['fal', 'check-circle'],
          label: 'Concluir',
        }
  }, [isLessonCompleted])

  const handlePreviousLessonPress = useCallback(async () => {
    if (!hasPreviousContent || !currentLesson?.id || !enrollment?.id) return
    try {
      setIsLoadingNextOrPreviousLesson(true)
      const { data } = await getPreviousAvailableLessonData(
        currentLesson?.id,
        enrollment?.id,
      )
      if (data?.previousAvailableLesson?.id) {
        setCurrentLesson(data.previousAvailableLesson)
        resetNextAndPreviousContent()
      }
    } catch (error) {
      toast.show('Erro ao buscar informações da aula anterior', {
        message: 'Tente novamente mais tarde',
        type: 'error',
      })
    } finally {
      setIsLoadingNextOrPreviousLesson(false)
    }
  }, [
    hasPreviousContent,
    currentLesson?.id,
    enrollment?.id,
    getPreviousAvailableLessonData,
    setCurrentLesson,
    resetNextAndPreviousContent,
    toast,
  ])

  const handleNextLessonPress = useCallback(async () => {
    if (!hasNextContent || !currentLesson?.id || !enrollment?.id) return
    try {
      setIsLoadingNextOrPreviousLesson(true)
      const { data } = await getNextAvailableLessonData(
        currentLesson?.id,
        enrollment?.id,
      )
      if (data?.nextAvailableLesson?.id) {
        setCurrentLesson(data.nextAvailableLesson)
        resetNextAndPreviousContent()
      }
    } catch (error) {
      toast.show('Erro ao buscar informações da próxima aula', {
        message: 'Tente novamente mais tarde',
        type: 'error',
      })
    } finally {
      setIsLoadingNextOrPreviousLesson(false)
    }
  }, [
    hasNextContent,
    currentLesson?.id,
    enrollment?.id,
    getNextAvailableLessonData,
    setCurrentLesson,
    resetNextAndPreviousContent,
    toast,
  ])

  const updateEnrollmentProgress = useCallback(() => {
    if (!enrollment || !enrollment?.progressCard?.lessonsProgresses) return

    const newLessonProgress = () => {
      if (
        enrollment?.progressCard?.lessonsProgresses?.find(
          (lessonProgress) => lessonProgress.lessonId === currentLesson?.id,
        )
      ) {
        return enrollment?.progressCard?.lessonsProgresses.map(
          (lessonProgress) => {
            if (lessonProgress.lessonId === currentLesson?.id) {
              return { ...lessonProgress, completed: true }
            }
            return lessonProgress
          },
        )
      } else {
        return [
          ...(enrollment?.progressCard?.lessonsProgresses ?? []),
          {
            id: currentLesson?.id ?? 0,
            lessonId: currentLesson?.id ?? 0,
            progress: 100,
            currentVideoTime: 0,
            lastViewAt: new Date().toISOString(),
            completed: true,
          },
        ] as ILessonsProgresses[]
      }
    }
    const newEnrollment = {
      ...enrollment,
      progressCard: {
        ...enrollment?.progressCard,
        completed: enrollment?.progressCard?.completed + 1,
        progress:
          ((enrollment?.progressCard?.completed + 1) /
            enrollment?.progressCard?.total) *
          100,
        lessonsProgresses: newLessonProgress(),
      },
    }
    updateEnrollmentState(newEnrollment)
  }, [enrollment, currentLesson?.id, updateEnrollmentState])

  const handleFinishLesson = useCallback(() => {
    if (!enrollment?.id || !currentLesson?.id) return
    try {
      handleUpdateLessonProgress({
        lessonId: currentLesson?.id,
        attributes: {
          completed: true,
          progress: 100,
        },
        enrollmentId: enrollment?.id,
      })
      setIsLessonCompleted(true)
      updateEnrollmentProgress()
    } catch (error) {
      console.error(error)
    }
  }, [
    currentLesson?.id,
    enrollment?.id,
    handleUpdateLessonProgress,
    updateEnrollmentProgress,
  ])

  useEffect(() => {
    if (currentLessonProgress?.completed && !isLessonCompleted) {
      setIsLessonCompleted(true)
    } else {
      if (shouldUpdateLessonProgressOnView) {
        handleFinishLesson()
      }
    }
  }, [
    currentLessonProgress?.completed,
    handleFinishLesson,
    isLessonCompleted,
    shouldUpdateLessonProgressOnView,
  ])

  const handlePress = (event: GestureResponderEvent) => {
    event.stopPropagation()
    setIsScrollingContent(false)
  }

  const handleUpdateProgress = useCallback(
    (progress: number, currentTime: number) => {
      if (!currentLesson?.id || !enrollment?.id || isLessonCompleted) return
      handleUpdateLessonProgress({
        lessonId: currentLesson?.id,
        attributes: {
          progress,
          currentVideoTime: currentTime,
        },
        enrollmentId: enrollment?.id,
      })
      if (progress === 100) {
        setIsLessonCompleted(true)
      }
    },
    [
      currentLesson?.id,
      enrollment?.id,
      isLessonCompleted,
      handleUpdateLessonProgress,
    ],
  )

  return (
    <YStack w="100%" onPress={handlePress} role="button">
      <XStack
        gap="$spacing-inset-sm"
        $gtSm={{
          mt: '$spacing-inset-lg',
          marginHorizontal: 48,
        }}
        h="100%"
      >
        <YStack flex={1} w="100%">
          <YStack
            backgroundColor="$neutral-0"
            p="$spacing-inset-xs"
            gap="$spacing-inset-xs"
            paddingBottom={
              lessonWithoutContent && !isTinyPlayer
                ? '$spacing-inset-xxl'
                : '$spacing-inset-xs'
            }
            maxHeight={!isActivity ? 750 : 'unset'}
            $gtSm={{
              borderTopLeftRadius: '$border-radius-md',
              borderTopRightRadius: '$border-radius-md',
              p: '$spacing-inset-sm',
            }}
          >
            <XStack
              gap="$spacing-inset-xs"
              w="100%"
              alignItems="center"
              justifyContent="space-between"
            >
              {mediaSize === 'sm' && (
                <XStack w="100%">
                  <CourseContentNavigation
                    visibilityType="top"
                    onPreviousPress={handlePreviousLessonPress}
                    onNextPress={handleNextLessonPress}
                    hasPreviousContent={hasPreviousContent}
                    hasNextContent={hasNextContent}
                    isLoading={isLoadingNextOrPreviousLesson}
                  />
                </XStack>
              )}
            </XStack>

            {/* Player Controls */}

            {!isActivity && extraMedia && (
              <XStack alignItems="center" justifyContent="center" w="100%">
                <MediaRenderWrapper media={extraMedia}>
                  <MediaRender
                    media={extraMedia}
                    isImageVariant={extraMedia.type === 'Image'}
                    thumbnailUrl={currentLesson?.coverImage?.cdnFileUrl}
                    startMediaFrom={startMediaFrom}
                    updateLessonProgress={({ progress, currentVideoTime }) =>
                      handleUpdateProgress(progress, currentVideoTime)
                    }
                    progress={currentLessonProgress?.progress}
                    fileUrl={
                      courseContent?.courseContent?.lesson?.media?.fileUrl
                    }
                  >
                    <NotSupported media={extraMedia} />
                  </MediaRender>
                </MediaRenderWrapper>
              </XStack>
            )}
            {isActivity && <ActivityPage />}
            {/* Content General Information */}
            {!isActivity && (
              <YStack
                gap="$spacing-inset-sm"
                paddingHorizontal="$spacing-inset-xxxs"
              >
                <YStack gap="$spacing-inset-sm">
                  <Text
                    color="$neutral-800"
                    fontSize="$font-size-lg"
                    fontFamily="$mulishBold"
                    fontWeight="700"
                    numberOfLines={2}
                  >
                    {currentLesson?.title || ''}
                  </Text>

                  <XStack alignItems="center" justifyContent="space-between">
                    <Pressable
                      testID="finish-lesson-button"
                      onPress={handleFinishLesson}
                      disabled={isLessonCompleted}
                    >
                      <YStack
                        gap="$spacing-inset-quarck"
                        alignItems="center"
                        p="$spacing-inset-nano"
                      >
                        <HeroIcon
                          icon={finishLessonButtonStyle.icon}
                          size={21}
                          color={finishLessonButtonStyle.iconColor}
                        />
                        <Text
                          color="$neutral-800"
                          fontSize="$font-size-sm"
                          fontFamily="$mulishRegular"
                          lineHeight={24}
                        >
                          {finishLessonButtonStyle.label}
                        </Text>
                      </YStack>
                    </Pressable>

                    {mediaSize === 'xs' && (
                      <CourseContentNavigation
                        onPreviousPress={handlePreviousLessonPress}
                        onNextPress={handleNextLessonPress}
                        hasPreviousContent={hasPreviousContent}
                        hasNextContent={hasNextContent}
                        isLoading={isLoadingNextOrPreviousLesson}
                      />
                    )}
                  </XStack>
                </YStack>
              </YStack>
            )}
          </YStack>
          <YStack paddingBottom={paddingBottom}>
            <TabsContentContainer
              currentLesson={currentLesson}
              currentTab={currentTab}
              onTabChange={setCurrentTab}
            />
          </YStack>
        </YStack>
      </XStack>
    </YStack>
  )
}
